import { acceptHMRUpdate, defineStore } from 'pinia'
import type { AxiosError, AxiosInstance } from 'axios'
import { API } from '../services'
import { errorTracker } from '../services/error/errorTracking'

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $axios: AxiosInstance
  }
}

export interface IndustryRankingItem {
  rank: number
  brandName: string
  mentions: number
  visibility: number
  isCurrentBrand?: boolean
}

export interface TopSource {
  id: string
  url: string
  domain: string
  title: string
  mentions: number
  lastSeen: string
  type: 'article' | 'forum' | 'social' | 'review' | 'other'
}

export interface ActionItem {
  id: string
  title: string
  description: string
  priority: 'high' | 'medium' | 'low'
  category: 'citations' | 'content' | 'seo' | 'social' | 'technical'
  impact: string
  effort: 'low' | 'medium' | 'high'
}

export interface LLMBreakdownItem {
  name: string
  percentage: number
  appeared_count: number
  total_prompts: number
  comingSoon: boolean
}

function normalizeModelName(rawName: string): string {
  return rawName
}

export interface AnalysisResult {
  id: string
  brandId: number
  industryRanking: IndustryRankingItem[]
  topSources: TopSource[]
  actionItems: ActionItem[]
  generatedAt: string
  status: 'processing' | 'completed' | 'failed'
}

const useAnalysisStore = defineStore('analysis', {
  state: () => ({
    error: undefined as unknown,
    analysis: null as AnalysisResult | null,
    isLoading: false,
    // Real analysis state
    realAnalysis: null as any[] | null,
    isRealAnalysisLoading: false,
    isPollingRealAnalysis: false,
    realAnalysisPollingInterval: null as NodeJS.Timeout | null,
    // Brand presence state
    brandPresence: null as any | null,
    isBrandPresenceLoading: false,
    // Global welcome analysis state
    isWelcomeAnalysisRunning: false,
    welcomeAnalysisCompleted: false,
    welcomeAnalysisBrandId: null as number | null,
    welcomeAnalysisPollingInterval: null as NodeJS.Timeout | null,
    isPollingWelcomeAnalysis: false,

  }),
  getters: {
    hasAnalysis: (state) => !!state.analysis,
    industryRanking: (state) => state.analysis?.industryRanking || [],
    topSources: (state) => state.analysis?.topSources || [],
    actionItems: (state) => state.analysis?.actionItems || [],
    // Real analysis getters
    hasRealAnalysis: (state) => !!state.realAnalysis && Array.isArray(state.realAnalysis) && state.realAnalysis.length > 0,
    realAnalysisCompetitors: (state) => {
      if (!state.realAnalysis) return []
      
      // Transform raw analysis data to competitors format
      const competitors = new Set<string>()
      const competitorMentions = new Map<string, number>()
      
      state.realAnalysis.forEach((promptResult: any) => {
        if (promptResult.companies && Array.isArray(promptResult.companies)) {
          promptResult.companies.forEach((company: any) => {
            if (company.name) {
              competitors.add(company.name)
              competitorMentions.set(company.name, (competitorMentions.get(company.name) || 0) + 1)
            }
          })
        }
      })
      
      // Convert to ranking format (rank only, as requested)
      return Array.from(competitors)
        .map((name, index) => ({
          rank: index + 1,
          brandName: name,
          mentions: competitorMentions.get(name) || 0,
          visibility: 0, // Not available in current data
          isCurrentBrand: false // We don't have current brand info in raw data
        }))
        .sort((a, b) => b.mentions - a.mentions) // Sort by mentions desc
        .map((item, index) => ({ ...item, rank: index + 1 })) // Re-rank after sorting
    },
    realAnalysisSources: (state) => {
      if (!state.realAnalysis) return []
      
      // Transform raw analysis data to sources format
      const sources = new Set<string>()
      const sourceData = new Map<string, any>()
      
      state.realAnalysis.forEach((promptResult: any) => {
        if (promptResult.parsed?.sources && Array.isArray(promptResult.parsed.sources)) {
          promptResult.parsed.sources.forEach((sourceUrl: string) => {
            if (sourceUrl && !sources.has(sourceUrl)) {
              sources.add(sourceUrl)
              
              // Extract domain from URL
              let domain = sourceUrl
              try {
                const url = new URL(sourceUrl)
                domain = url.hostname
              } catch (e) {
                // Keep original if URL parsing fails
              }
              
              sourceData.set(sourceUrl, {
                id: `source_${sources.size}`,
                url: sourceUrl,
                domain: domain,
                title: `Source from ${domain}`, // We don't have titles in raw data
                mentions: 1, // Each source appears once per prompt
                lastSeen: new Date().toISOString(), // We don't have this data
                type: 'other' as const // We don't have type classification
              })
            } else if (sourceUrl && sources.has(sourceUrl)) {
              // Increment mentions for existing source
              const existing = sourceData.get(sourceUrl)
              if (existing) {
                existing.mentions += 1
              }
            }
          })
        }
      })
      
      return Array.from(sourceData.values())
        .sort((a, b) => b.mentions - a.mentions) // Sort by mentions desc
    },
    // Visibility getters (uses brand presence data)
    overallVisibility: (state) => {
      // If we have brand presence data, use it instead
      if (state.brandPresence?.presence && state.brandPresence.presence.length > 0) {
        return state.brandPresence.presence.map((day: any) => {
          // Calculate overall appearance rate across all models for this day
          let totalAppeared = 0
          let totalPrompts = 0
          
          day.models.forEach((model: any) => {
            totalAppeared += model.appeared_count || 0
            totalPrompts += model.total_prompts || 0
          })
          
          const visibility_overall = totalPrompts > 0 
            ? Math.round((totalAppeared / totalPrompts) * 100 * 100) / 100 
            : 0
          
          return {
            date: day.date,
            visibility_overall
          }
        })
      }
      
      return []
    },
    getPromptVisibility: (state) => (promptId: number) => {
      // Get visibility from brand presence (last run data)
      if (!state.brandPresence?.presence || state.brandPresence.presence.length === 0) {
        return 0
      }
      
      // Get the most recent date's data (last run)
      const sortedPresence = [...state.brandPresence.presence].sort((a: any, b: any) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
      )
      const lastRunData = sortedPresence[0]
      
      if (!lastRunData?.models || lastRunData.models.length === 0) {
        return 0
      }
      
      // Calculate visibility: how many models showed the brand for this prompt
      let appearedCount = 0
      let totalModels = 0
      
      lastRunData.models.forEach((model: any) => {
        const promptEntry = model.prompts?.find((p: any) => p.prompt_id === promptId)
        if (promptEntry) {
          totalModels++
          if (promptEntry.count > 0) {
            appearedCount++
          }
        }
      })
      
      return totalModels > 0 ? Math.round((appearedCount / totalModels) * 100) : 0
    },
    getPromptVisibleModels: (state) => (promptId: number): string[] => {
      if (!state.brandPresence?.presence || state.brandPresence.presence.length === 0) {
        return []
      }

      const sortedPresence = [...state.brandPresence.presence].sort((a: any, b: any) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
      )
      const lastRunData = sortedPresence[0]

      if (!lastRunData?.models || lastRunData.models.length === 0) {
        return []
      }

      const visibleModelsSet = new Set<string>()
      lastRunData.models.forEach((model: any) => {
        const promptEntry = model.prompts?.find((p: any) => p.prompt_id === promptId)
        if (promptEntry && promptEntry.count > 0) {
          // Presence payload returns model display name under `name`
          if (model.name) {
            visibleModelsSet.add(model.name)
          }
        }
      })

      return Array.from(visibleModelsSet)
    },
    // Brand visibility getters - use presence data
    getBrandTotalAppearances: (state) => {
      if (!state.brandPresence?.presence || state.brandPresence.presence.length === 0) {
        return 0
      }
      
      let totalAppeared = 0
      state.brandPresence.presence.forEach((day: any) => {
        day.models.forEach((model: any) => {
          totalAppeared += model.appeared_count || 0
        })
      })
      return totalAppeared
    },
    getBrandOverallVisibility: (state) => {
      if (!state.brandPresence?.presence || state.brandPresence.presence.length === 0) {
        return 0
      }
      
      let totalAppeared = 0
      let totalPrompts = 0
      
      state.brandPresence.presence.forEach((day: any) => {
        day.models.forEach((model: any) => {
          totalAppeared += model.appeared_count || 0
          totalPrompts += model.total_prompts || 0
        })
      })
      
      return totalPrompts > 0 ? Math.round((totalAppeared / totalPrompts) * 100) : 0
    },
    // Brand presence getters - LLM breakdown
    getLLMBreakdown: (state): LLMBreakdownItem[] => {
      console.log('[getLLMBreakdown] Called - brandPresence state:', state.brandPresence)
      
      if (!state.brandPresence?.presence || state.brandPresence.presence.length === 0) {
        console.log('[getLLMBreakdown] No presence data available')
        return []
      }

      console.log('[getLLMBreakdown] Processing presence data:', state.brandPresence.presence)
      
      // Aggregate data per model across all dates
      const modelAggregates = new Map<string, { appeared: number; total: number }>()
      
      state.brandPresence.presence.forEach((day: any) => {
        console.log('[getLLMBreakdown] Processing day:', day)
        day.models.forEach((model: any) => {
          console.log('[getLLMBreakdown] Processing model:', model)
          const normalizedName = normalizeModelName(model.name)
          console.log('[getLLMBreakdown] Normalized name:', model.name, '->', normalizedName)
          const existing = modelAggregates.get(normalizedName) || { appeared: 0, total: 0 }
          
          modelAggregates.set(normalizedName, {
            appeared: existing.appeared + (model.appeared_count || 0),
            total: existing.total + (model.total_prompts || 0)
          })
        })
      })
      
      console.log('[getLLMBreakdown] Model aggregates:', Array.from(modelAggregates.entries()))

      const result = Array.from(modelAggregates.entries())
        .filter(([, data]) => data.total > 0)
        .map(([name, data]) => ({
          name,
          percentage: Math.round((data.appeared / data.total) * 100),
          appeared_count: data.appeared,
          total_prompts: data.total,
          comingSoon: false
        }))
        .sort((a, b) => b.percentage - a.percentage)
      
      console.log('[getLLMBreakdown] Final result:', result)
      return result
    },
    // Overall presence percentage across all LLMs
    getOverallPresencePercentage: (state): number => {
      console.log('[getOverallPresencePercentage] Called - brandPresence state:', state.brandPresence)
      
      if (!state.brandPresence?.presence || state.brandPresence.presence.length === 0) {
        console.log('[getOverallPresencePercentage] No presence data, returning 0')
        return 0
      }

      // Sum up all appearances and prompts across all models and dates
      let totalAppeared = 0
      let totalPrompts = 0
      
      state.brandPresence.presence.forEach((day: any) => {
        day.models.forEach((model: any) => {
          totalAppeared += model.appeared_count || 0
          totalPrompts += model.total_prompts || 0
        })
      })
      
      const percentage = totalPrompts > 0 ? Math.round((totalAppeared / totalPrompts) * 100) : 0
      console.log('[getOverallPresencePercentage] Result:', {
        totalAppeared,
        totalPrompts,
        percentage
      })
      
      return percentage
    },
  },
  actions: {
    async initBrandAnalysis(brandId: number) {
      try {
        this.isLoading = true
        const response = await API.business.initBrandAnalysis(brandId)

        console.log('init analysis response:', response)
        if (response.status === 200) {
          return {
            success: true,
            message: response.data.message
          }
        }

        throw new Error('Something went wrong')
      } catch (error) {
        const _error = error as AxiosError<any>
        
        errorTracker.captureError(error as Error, {
          componentName: 'AnalysisStore',
          action: 'initBrandAnalysis'
        })
        
        return {
          success: false,
          message: _error.response?.data?.error || 'Failed to initiate brand analysis'
        }
      } finally {
        this.isLoading = false
      }
    },

    async getBrandAnalysis(brandId: number) {
      try {
        this.isLoading = true
        const response = await API.business.getBrandAnalysis(brandId)

        console.log('get analysis response:', response)
        if (response.status === 200) {
          if (response.data.analysis) {
            this.analysis = response.data.analysis
          }
          
          return {
            success: true,
            analysis: response.data.analysis,
            ready: response.data.ready,
            message: response.data.message
          }
        }

        throw new Error('Something went wrong')
      } catch (error) {
        const _error = error as AxiosError<any>
        
        // If 404, it means analysis doesn't exist yet
        if (_error.response?.status === 404) {
          console.log('404 from catch handler - no analysis found')
          this.analysis = null
          return {
            success: true,
            analysis: null,
            ready: false,
            message: 'Analysis not found'
          }
        }

        errorTracker.captureError(error as Error, {
          componentName: 'AnalysisStore',
          action: 'getBrandAnalysis'
        })
        
        return {
          success: false,
          message: _error.response?.data?.error || 'Failed to retrieve brand analysis'
        }
      } finally {
        this.isLoading = false
      }
    },

    // Real analysis actions
    async initRealBrandAnalysis(brandId: number) {
      try {
        this.isRealAnalysisLoading = true
        const response = await API.business.initRealBrandAnalysis(brandId)

        console.log('init real analysis response:', response)
        if (response.status === 200) {
          return {
            success: true,
            message: response.data.message,
            jobId: response.data.jobId
          }
        }

        throw new Error('Something went wrong')
      } catch (error) {
        const _error = error as AxiosError<any>
        
        errorTracker.captureError(error as Error, {
          componentName: 'AnalysisStore',
          action: 'initRealBrandAnalysis'
        })
        
        return {
          success: false,
          message: _error.response?.data?.error || 'Failed to initiate real brand analysis'
        }
      } finally {
        this.isRealAnalysisLoading = false
      }
    },

    async getRealBrandAnalysis(brandId: number) {
      try {
        this.isRealAnalysisLoading = true
        const response = await API.business.getRealBrandAnalysis(brandId)

        console.log('get real analysis response:', response)
        if (response.status === 200) {
          if (response.data.analysis) {
            this.realAnalysis = response.data.analysis
          }
          
          return {
            success: true,
            analysis: response.data.analysis,
            ready: response.data.ready,
            message: response.data.message
          }
        }

        throw new Error('Something went wrong')
      } catch (error) {
        const _error = error as AxiosError<any>
        
        // If 404, it means analysis doesn't exist yet or not ready
        if (_error.response?.status === 404) {
          console.log('404 from catch handler - real analysis not ready')
          this.realAnalysis = null
          return {
            success: true,
            analysis: null,
            ready: false,
            message: 'Real analysis not ready yet'
          }
        }

        errorTracker.captureError(error as Error, {
          componentName: 'AnalysisStore',
          action: 'getRealBrandAnalysis'
        })
        
        return {
          success: false,
          message: _error.response?.data?.error || 'Failed to retrieve real brand analysis'
        }
      } finally {
        this.isRealAnalysisLoading = false
      }
    },

    async startRealAnalysisPolling(brandId: number) {
      // Clear any existing polling
      this.stopRealAnalysisPolling()
      
      this.isPollingRealAnalysis = true
      
      // Check immediately first
      const initialResult = await this.getRealBrandAnalysis(brandId)
      if (initialResult.success && initialResult.ready) {
        this.isPollingRealAnalysis = false
        return
      }
      
      // Start polling every 8 seconds
      this.realAnalysisPollingInterval = setInterval(async () => {
        try {
          const result = await this.getRealBrandAnalysis(brandId)
          
          if (result.success && result.ready) {
            // Analysis is ready, stop polling
            this.stopRealAnalysisPolling()
          }
        } catch (error) {
          console.error('Error during real analysis polling:', error)
          // Continue polling despite error
        }
      }, 8000)
    },

    stopRealAnalysisPolling() {
      if (this.realAnalysisPollingInterval) {
        clearInterval(this.realAnalysisPollingInterval)
        this.realAnalysisPollingInterval = null
      }
      this.isPollingRealAnalysis = false
    },

    async checkAndRestartRealAnalysisPolling(brandId: number) {
      try {
        // Check if there's a running job for this brand
        const jobStatus = await API.business.checkBrandJobStatus(brandId, 'run_prompts')
        
        if (jobStatus.status === 200 && jobStatus.data.success && jobStatus.data.status === 'RUNNING') {
          console.log('Found running real analysis job, restarting polling')
          await this.startRealAnalysisPolling(brandId)
          return true
        }
        
        return false
      } catch (error) {
        console.error('Error checking real analysis job status:', error)
        return false
      }
    },

    clearRealAnalysis() {
      this.realAnalysis = null
      this.stopRealAnalysisPolling()
    },

    clearAnalysis() {
      this.analysis = null
      this.error = undefined
    },

    clearBrandScopedState() {
      this.clearRealAnalysis()
      this.clearAnalysis()
      this.brandPresence = null
      this.isBrandPresenceLoading = false
      this.isRealAnalysisLoading = false
    },

    // Brand presence actions
    async getBrandPresence(brandId: number, days: number = 30, promptId?: number) {
      try {
        this.isBrandPresenceLoading = true
        const response = await API.business.getBrandPresence(brandId, days, promptId)

        console.log('get brand presence response:', response)
        if (response.status === 200) {
          if (response.data?.data) {
            this.brandPresence = response.data.data
          }
          
          return {
            success: true,
            data: response.data?.data,
            message: response.data?.message || 'Brand presence data retrieved successfully'
          }
        }

        throw new Error('Something went wrong')
      } catch (error) {
        const _error = error as AxiosError<any>
        
        // If 404, it means no presence data available yet
        if (_error.response?.status === 404) {
          console.log('404 from catch handler - no brand presence data found')
          this.brandPresence = null
          return {
            success: true,
            data: null,
            message: 'No brand presence data available yet'
          }
        }

        errorTracker.captureError(error as Error, {
          componentName: 'AnalysisStore',
          action: 'getBrandPresence'
        })
        
        return {
          success: false,
          message: _error.response?.data?.error || 'Failed to retrieve brand presence data'
        }
      } finally {
        this.isBrandPresenceLoading = false
      }
    },

    clearBrandPresence() {
      this.brandPresence = null
    },

    // Global welcome analysis actions
    startWelcomeAnalysis(brandId: number) {
      this.isWelcomeAnalysisRunning = true
      this.welcomeAnalysisCompleted = false
      this.welcomeAnalysisBrandId = brandId
    },

    completeWelcomeAnalysis() {
      this.welcomeAnalysisCompleted = true
      this.isWelcomeAnalysisRunning = false
    },

    resetWelcomeAnalysis() {
      this.isWelcomeAnalysisRunning = false
      this.welcomeAnalysisCompleted = false
      this.welcomeAnalysisBrandId = null
    },

    // Welcome analysis polling
    async startWelcomeAnalysisPolling() {
      this.stopWelcomeAnalysisPolling()
      
      if (!this.isWelcomeAnalysisRunning || this.welcomeAnalysisCompleted) {
        return
      }

      this.isPollingWelcomeAnalysis = true

      this.welcomeAnalysisPollingInterval = setInterval(async () => {
        try {
          const { getUpgradeTransitionStatus } = await import('../services/welcomeAnalysis')
          const status = await getUpgradeTransitionStatus()
          
          if (status.status === 'completed') {
            this.completeWelcomeAnalysis()
            this.stopWelcomeAnalysisPolling()
          } else if (status.status === 'pending' || status.status === null) {
            this.resetWelcomeAnalysis()
            this.stopWelcomeAnalysisPolling()
          }
        } catch (error) {
          console.error('Error polling welcome analysis status:', error)
        }
      }, 5000)
    },

    stopWelcomeAnalysisPolling() {
      if (this.welcomeAnalysisPollingInterval) {
        clearInterval(this.welcomeAnalysisPollingInterval)
        this.welcomeAnalysisPollingInterval = null
      }
      this.isPollingWelcomeAnalysis = false
    }
  }
})

export default useAnalysisStore

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAnalysisStore, import.meta.hot))
}
